import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dropdown } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan, useSelectedLocation } from "components";
import "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/components/AddServicePendingChecklist/AddServicePendingChecklist.scss";
import { useAddServicePendingChecklistMutation } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/components/AddServicePendingChecklist/hooks";
import { useChecklistDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { Checklist } from "models";
import type { SortableServicePendingChecklist } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components";
import type { GroupedChecklistsDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";

type AddServicePendingChecklistProps = {
  items: SortableServicePendingChecklist[];
  nextOrdering: number;
  checklists?: Checklist[];
  checkInServiceId: number;
};

export const AddServicePendingChecklist = ({ nextOrdering, items, checklists, checkInServiceId }: AddServicePendingChecklistProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { mutate: addServicePendingChecklist } = useAddServicePendingChecklistMutation();
  const canCreatePendingChecklist = useCan("create", "check-in-service-pending-checklist");
  const { groupedChecklistsDropdownOptions } = useChecklistDropdownOptions(checklists);

  const [selectedChecklistId, setSelectedChecklistId] = useState(0);

  const excludeSelectedChecklistDropdownOptions = groupedChecklistsDropdownOptions.reduce<GroupedChecklistsDropdownOptions[]>((acc, cdo) => {
    if (!items.some(i => i.checklist_id === cdo.key)) acc.push(cdo);
    return acc;
  }, []);

  const handleAddItem = () => {
    if (selectedChecklistId) {
      const selectedChecklist = checklists?.find(cl => cl.id === selectedChecklistId);

      if (selectedChecklist && selectedLocation) {
        setSelectedChecklistId(0);
        addServicePendingChecklist({
          checkin_service_id: checkInServiceId,
          ordering: nextOrdering,
          checklist_name: selectedChecklist.name,
          dealer_location_id: selectedLocation.id,
          checklist_id: selectedChecklist.id,
          created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
          updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
        });
      }
    }
  };

  return (
    <div className="AddServicePendingChecklist">
      <Dropdown
        fluid
        selection
        clearable
        search
        selectOnBlur={false}
        disabled={!canCreatePendingChecklist}
        options={excludeSelectedChecklistDropdownOptions}
        value={selectedChecklistId > 0 ? selectedChecklistId : ""}
        placeholder={t("v8_select_mandatory_checklists", "Select mandatory checklists")}
        onChange={(_e, { value }) => setSelectedChecklistId(Number(value) || 0)}
      />
      <Button type="button" color="green" className="add-button" onClick={handleAddItem} disabled={!canCreatePendingChecklist || !selectedChecklistId}>
        {t("v8_add", "Add")}
      </Button>
    </div>
  );
};
